import { useState } from 'react';

import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';

import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import { useStyleRules } from 'components/modules/style-rules/hooks';
import {
  selectAllThemeTemplates,
  setAllThemeTemplates,
  updateThemeTemplate,
  addThemeTemplate,
} from 'components/modules/theme-templates/slices/theme-templates';
import API from 'libs/api';
import ErrorHandler, { getErrorMessage } from 'libs/errors/errors';
import { selectActiveRecipeName, selectWebsitePatches, selectWebsiteId } from 'selectors/website';

export default function useThemeTemplates({ forceFetch = false } = {}) {
  const websiteId = useSelector(selectWebsiteId);
  const patches = useSelector(selectWebsitePatches);
  const recipe = selectActiveRecipeName(patches);
  const themeTemplates = useSelector(selectAllThemeTemplates);
  const dispatch = useDispatch();
  const { getStyleRules, styleRules } = useStyleRules();

  const [templateType, setTemplateType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function fetchTemplates() {
    setLoading(true);
    setError('');

    try {
      const params = { recipe, type: templateType, query: searchQuery };
      const { data: templates } = await API.getThemeTemplates(params);
      dispatch(setAllThemeTemplates(templates));
    } catch (e) {
      ErrorHandler.capture(e);
      setError(getErrorMessage(e));
    }

    setLoading(false);
  }

  async function createThemeTemplate(template) {
    const { data: newTemplate } = await API.createThemeTemplate({ ...template, recipes: [recipe] });

    dispatch(addThemeTemplate(newTemplate));
  }

  async function editThemeTemplate(template) {
    const { id: templateId } = template;
    const { data: nextTemplate } = await API.updateThemeTemplate(templateId, template);

    dispatch(updateThemeTemplate({ id: templateId, changes: nextTemplate }));
  }

  async function applyThemeTemplate(template) {
    const { id: templateId } = template;
    const { options, overrides } = template;

    const payload = {
      website: websiteId,
      options: { ...options, overrides },
    };

    await API.applyThemeTemplate(templateId, payload);
    await getStyleRules();
  }

  function onSearchQueryChange(e, { value: nextSearchQuery }) {
    setSearchQuery(nextSearchQuery);
  }

  function onTemplateTypeChange(e, { value: nextTemplateType }) {
    setTemplateType(nextTemplateType);
  }

  function createTemplateFromWebsite() {
    const templateStyleRules = styleRules.map(({ name, value }) => ({
      name,
      value,
    }));

    return {
      name: '',
      description: '',
      tags: [],
      styleRules: templateStyleRules,
    };
  }

  useAsyncEffect(async () => {
    if (_isEmpty(themeTemplates) || forceFetch) {
      await fetchTemplates();
    }
  }, [searchQuery, templateType]);

  return {
    loading,
    error,
    templateType,
    searchQuery,
    themeTemplates,
    onSearchQueryChange,
    createThemeTemplate,
    editThemeTemplate,
    applyThemeTemplate,
    createTemplateFromWebsite,
    onTemplateTypeChange,
  };
}
